import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import "./imageSlider.css";

const DEFAULT_IMAGE = "/images/logo_teccc_no_disponible.png";

/** YouTube, Vimeo, Dailymotion, Facebook y Twitch. */
const VIDEO_PLATFORM = /(?:youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com|dai\.ly|facebook\.com\/.+(?:videos|watch)|fb\.watch|twitch\.tv)/i;

const mediaKind = (url, videoOn, pdfOn) => {
  const value = String(url || "");
  if (pdfOn && /\.pdf(?:$|[?#])/i.test(value)) return "pdf";
  if (videoOn && VIDEO_PLATFORM.test(value)) return "video";
  return "image";
};

const ImageSlider = ({ slides }) => {
  const { t } = useTranslation();
  const { serverBackendDomain, gallerySlideSeconds, galleryVideoEnabled, galleryPdfEnabled } = useSelector(
    (state) => state.configuration
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [failedImages, setFailedImages] = useState({});
  const [reelPaused, setReelPaused] = useState(false);

  const seconds = Math.max(1, Number(gallerySlideSeconds) || 8);
  const slideDuration = seconds * 1000;
  const videoOn = String(galleryVideoEnabled || "true").toLowerCase() === "true";
  const pdfOn = String(galleryPdfEnabled || "true").toLowerCase() === "true";

  const goToNext = useCallback(() => {
    setCurrentIndex((index) => (index === slides.length - 1 ? 0 : index + 1));
    setProgress(0);
    setReelPaused(false);
  }, [slides.length]);

  const goToPrevious = (event) => {
    event?.preventDefault();
    setCurrentIndex((index) => (index === 0 ? slides.length - 1 : index - 1));
    setProgress(0);
    setReelPaused(false);
  };

  const hasReel = (slides?.length || 0) > 1;

  useEffect(() => {
    if (!hasReel || reelPaused) return undefined;
    const step = 100;
    const increment = (step / slideDuration) * 100;
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          goToNext();
          return 0;
        }
        return oldProgress + increment;
      });
    }, step);
    return () => clearInterval(timer);
  }, [goToNext, hasReel, reelPaused, slideDuration]);

  const getOriginalUrl = (slide) => {
    if (!slide) return "";
    return slide.has_file ? `${serverBackendDomain}/${slide.path}` : slide.url;
  };

  if (!slides || slides.length === 0) return null;

  return (
    <div className="slider-wrapper">
      <div className="slider-container">
        {hasReel && (
          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
        )}

        {hasReel && (
          <>
            <button type="button" className="arrow-btn left" onClick={goToPrevious}>❮</button>
            <button type="button" className="arrow-btn right" onClick={() => goToNext()}>❯</button>
          </>
        )}

        {slides.map((slide, index) => {
          const originalUrl = getOriginalUrl(slide);
          const kind = mediaKind(originalUrl, videoOn, pdfOn);
          const displayUrl = failedImages[index] ? DEFAULT_IMAGE : originalUrl;
          const showImage = kind === "image";

          return (
            <div
              key={index}
              className={`slide-fade ${index === currentIndex ? "active" : ""}`}
              style={showImage ? { backgroundImage: `url(${displayUrl})` } : undefined}
            >
              {showImage && !failedImages[index] && (
                <img
                  src={originalUrl}
                  alt=""
                  style={{ display: "none" }}
                  onError={() => {
                    setFailedImages((prev) => ({ ...prev, [index]: true }));
                  }}
                />
              )}

              {kind === "video" && index === currentIndex && (
                <div className="slide-player">
                  <ReactPlayer
                    url={originalUrl}
                    width="100%"
                    height="100%"
                    controls
                    onPlay={() => setReelPaused(true)}
                    onPause={() => setReelPaused(false)}
                    onEnded={() => setReelPaused(false)}
                  />
                </div>
              )}

              {kind === "pdf" && index === currentIndex && (
                <div className="slide-pdf">
                  <p className="slide-pdf-label">PDF</p>
                  <a href={originalUrl} target="_blank" rel="noopener noreferrer">
                    {originalUrl}
                  </a>
                </div>
              )}

              {index === currentIndex && (
                <div className="slide-caption">
                  <div className="caption-content">
                    <p><strong>{t("components.slider.name")}:</strong> {slide.name}</p>
                    <p><strong>{t("components.slider.description")}:</strong> {slide.description}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasReel && (
        <div className="dots-container">
          {slides.map((_, index) => (
            <div
              key={index}
              className={`dot ${index === currentIndex ? "active" : ""}`}
              onClick={() => {
                setCurrentIndex(index);
                setProgress(0);
                setReelPaused(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;
